import { useState, useRef, useEffect, useCallback } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { authHeaders } from './listPageStyles';

marked.setOptions({ gfm: true, breaks: true });

function renderMarkdown(text) {
  const html = marked.parse(text ?? '');
  return DOMPurify.sanitize(typeof html === 'string' ? html : '');
}

const unwrap = (payload) => payload?.Data ?? payload?.data;

const EXAMPLES = [
  "Bursa'daki 5 müşteriyi listele",
  'Satış KDV oranı %20 olan ürünler',
  'Bu ay brüt tutarı 1000 üzeri siparişler',
];

const styles = {
  shell: { display: 'flex', gap: 12, height: 'calc(100vh - 110px)', minHeight: 480 },

  // Sol panel — konuşma listesi
  panel: {
    width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #E9EDF7)',
    borderRadius: 14, overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 14px', borderBottom: '1px solid var(--border-color, #E9EDF7)',
  },
  panelTitle: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #2B3674)' },
  newBtn: {
    background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8,
    padding: '6px 10px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  convList: { flex: 1, overflowY: 'auto', padding: 6 },
  convItem: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary, #2B3674)',
  },
  convItemActive: { background: 'rgba(0,115,234,0.10)' },
  convTitle: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  iconBtn: {
    border: 'none', background: 'transparent', cursor: 'pointer',
    color: 'var(--text-secondary, #A3AED0)', fontSize: 13, padding: '2px 4px', lineHeight: 1,
  },
  panelEmpty: { padding: 16, fontSize: 12, color: 'var(--text-secondary, #A3AED0)', textAlign: 'center' },

  // Sağ — sohbet
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  header: { padding: '4px 4px 12px' },
  title: { margin: 0, fontSize: 22, color: 'var(--text-primary, #2B3674)' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary, #A3AED0)' },
  messages: {
    flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 4px',
  },
  row: { display: 'flex', width: '100%' },
  rowUser: { justifyContent: 'flex-end' },
  rowAssistant: { justifyContent: 'flex-start' },
  bubbleBase: {
    maxWidth: '78%', padding: '10px 14px', borderRadius: 14, fontSize: 14,
    lineHeight: 1.55, wordBreak: 'break-word', whiteSpace: 'pre-wrap',
  },
  bubbleUser: { background: 'var(--accent)', color: '#fff', borderBottomRightRadius: 4 },
  bubbleAssistant: {
    background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #2B3674)',
    border: '1px solid var(--border-color, #E9EDF7)', borderBottomLeftRadius: 4, whiteSpace: 'normal',
  },
  bubbleError: {
    background: 'rgba(220,38,38,0.08)', color: '#b42318',
    border: '1px solid rgba(220,38,38,0.25)', borderBottomLeftRadius: 4, whiteSpace: 'normal',
  },
  tools: { marginTop: 6, fontSize: 11, color: 'var(--text-secondary, #A3AED0)' },
  empty: {
    margin: 'auto', textAlign: 'center', color: 'var(--text-secondary, #A3AED0)',
    display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center',
  },
  chip: {
    cursor: 'pointer', background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #E9EDF7)',
    color: 'var(--text-primary, #2B3674)', borderRadius: 999, padding: '8px 14px', fontSize: 13,
  },
  inputBar: {
    display: 'flex', gap: 8, alignItems: 'flex-end', padding: '12px 4px 4px',
    borderTop: '1px solid var(--border-color, #E9EDF7)',
  },
  textarea: {
    flex: 1, resize: 'none', minHeight: 44, maxHeight: 160, padding: '12px 14px',
    borderRadius: 12, border: '1px solid var(--border-color, #E9EDF7)',
    background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #2B3674)',
    fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5,
  },
  send: {
    height: 44, padding: '0 22px', border: 'none', borderRadius: 12, background: 'var(--accent)',
    color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  sendDisabled: { background: '#9db8f6', cursor: 'not-allowed' },
};

export function AssistantChatPage() {
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // { role, text, error?, toolCalls? }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch('/api/assistant/conversations', { headers: authHeaders() });
      const payload = await res.json();
      const list = unwrap(payload);
      setConversations(Array.isArray(list) ? list : []);
    } catch {
      /* sessizce geç */
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const openConversation = useCallback(async (id) => {
    try {
      const res = await fetch(`/api/assistant/conversations/${id}`, { headers: authHeaders() });
      const payload = await res.json();
      const env = unwrap(payload);
      if (env) {
        const msgs = (env.Messages ?? env.messages ?? []).map((m) => ({
          role: m.Role ?? m.role,
          text: m.Text ?? m.text,
        }));
        setMessages(msgs);
        setConversationId(env.Id ?? env.id ?? id);
      }
    } catch {
      /* sessizce geç */
    }
  }, []);

  const newChat = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    inputRef.current?.focus();
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message: text, conversationId }),
      });

      let payload = null;
      try { payload = await res.json(); } catch { /* JSON değilse aşağıda yakalanır */ }

      const env = unwrap(payload);
      const ok = res.ok && payload?.Success !== false && payload?.success !== false;

      if (!ok) {
        const msg = payload?.Message ?? payload?.message ?? `Bir hata oluştu (${res.status}).`;
        setMessages((m) => [...m, { role: 'assistant', text: msg, error: true }]);
      } else {
        const answer = env?.Answer ?? env?.answer ?? 'Yanıt alınamadı.';
        const convId = env?.ConversationId ?? env?.conversationId ?? null;
        const toolCalls = env?.ToolCalls ?? env?.toolCalls ?? [];
        setMessages((m) => [...m, { role: 'assistant', text: answer, toolCalls }]);
        if (convId) setConversationId(convId);
        loadConversations(); // başlık + sıralama güncellensin
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.', error: true }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, conversationId, loadConversations]);

  const deleteConversation = useCallback(async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu konuşma silinsin mi?')) return;
    try {
      await fetch(`/api/assistant/conversations/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (id === conversationId) newChat();
      loadConversations();
    } catch {
      /* sessizce geç */
    }
  }, [conversationId, newChat, loadConversations]);

  const renameConversation = useCallback(async (id, currentTitle, e) => {
    e.stopPropagation();
    const title = window.prompt('Yeni başlık:', currentTitle || '');
    if (title == null) return;
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      await fetch(`/api/assistant/conversations/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ title: trimmed }),
      });
      loadConversations();
    } catch {
      /* sessizce geç */
    }
  }, [loadConversations]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const applyExample = (q) => {
    setInput(q);
    inputRef.current?.focus();
  };

  const canSend = input.trim().length > 0 && !loading;

  return (
    <div style={styles.shell}>
      {/* Sol panel */}
      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <span style={styles.panelTitle}>Sohbetler</span>
          <button type="button" style={styles.newBtn} onClick={newChat}>+ Yeni</button>
        </div>
        <div style={styles.convList}>
          {conversations.length === 0 && <div style={styles.panelEmpty}>Henüz konuşma yok.</div>}
          {conversations.map((c) => {
            const id = c.Id ?? c.id;
            const title = c.Title ?? c.title ?? '(başlıksız)';
            const active = id === conversationId;
            return (
              <div
                key={id}
                style={{ ...styles.convItem, ...(active ? styles.convItemActive : null) }}
                onClick={() => openConversation(id)}
                title={title}
              >
                <span style={styles.convTitle}>{title}</span>
                <button type="button" style={styles.iconBtn} title="Yeniden adlandır"
                        onClick={(e) => renameConversation(id, title, e)}>✎</button>
                <button type="button" style={styles.iconBtn} title="Sil"
                        onClick={(e) => deleteConversation(id, e)}>×</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sağ — sohbet */}
      <div style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>AI Asistan</h1>
          <p style={styles.subtitle}>Verileriniz hakkında doğal dille soru sorun (salt-okunur).</p>
        </div>

        <div style={styles.messages}>
          {messages.length === 0 && !loading && (
            <div style={styles.empty}>
              <div style={{ fontSize: 32 }}>💬</div>
              <div>Başlamak için bir örnek seçin veya kendi sorunuzu yazın.</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {EXAMPLES.map((q) => (
                  <span key={q} style={styles.chip} onClick={() => applyExample(q)}>{q}</span>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            const bubble = {
              ...styles.bubbleBase,
              ...(isUser ? styles.bubbleUser : m.error ? styles.bubbleError : styles.bubbleAssistant),
            };
            return (
              <div key={i} style={{ ...styles.row, ...(isUser ? styles.rowUser : styles.rowAssistant) }}>
                <div style={bubble}>
                  {isUser || m.error ? (
                    m.text
                  ) : (
                    <div className="chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                  )}
                  {!isUser && Array.isArray(m.toolCalls) && m.toolCalls.length > 0 && (
                    <div style={styles.tools}>
                      🔧 {m.toolCalls.map((t) => t.Name ?? t.name).filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {loading && (
            <div style={{ ...styles.row, ...styles.rowAssistant }}>
              <div style={{ ...styles.bubbleBase, ...styles.bubbleAssistant, color: 'var(--text-secondary, #A3AED0)' }}>
                yazıyor…
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        <div style={styles.inputBar}>
          <textarea
            ref={inputRef}
            style={styles.textarea}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Sorunuzu yazın… (Enter ile gönder, Shift+Enter ile yeni satır)"
            rows={1}
            disabled={loading}
          />
          <button
            type="button"
            style={{ ...styles.send, ...(canSend ? null : styles.sendDisabled) }}
            onClick={send}
            disabled={!canSend}
          >
            Gönder
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssistantChatPage;
